import React, { useCallback, useMemo, useState } from 'react';
import moment, { Moment } from 'moment-timezone';

import { AverageDischargeChart } from '@components/chart/averageDischargeChart';
import { useTranslation } from '@lib/useTypedTranslation';
import { RequestInitWithRetry } from '@lib/request';
import { getSelectedTzDateDeviceChargeLevel, IDeviceDischarge, ITzBatteryLevel } from '../../../services/core/devicePerformance';
import { useWorldRequest } from '@lib/useWorldRequest';
import { Loading } from '@components/loading/loading';
import { DeviceLevelChart } from './deviceLevelChart';
import { getDisplayTimeZone } from '../../../lib/dateParams';

import './styles.css';


interface IProps {
  id: string,
  data: IDeviceDischarge[],
  isLoading?: boolean,
  /** IANA name of the device's time zone. Defaults to UTC when absent. */
  timeZone?: string
}

export function DeviceCharts(props: IProps) {
  const { t } = useTranslation(['performance', 'translation']);
  const displayTimeZone = getDisplayTimeZone(props.timeZone);
  const [deviceBatteryLevels, setDeviceBatteryLevels] = useState<ITzBatteryLevel[]>();
  // The selected day is a local calendar date in the device's zone. `undefined` means "today in
  // that zone". It is only turned into a real UTC instant (the end of that local day) below, so
  // 23 and 25 hour days on DST transitions select the correct real range.
  const [selectedDayKey, setSelectedDayKey] = useState<string | undefined>(undefined);
  const deviceLevelDate = useMemo(
    () => (selectedDayKey ? moment.tz(selectedDayKey, displayTimeZone) : moment.utc().tz(displayTimeZone)).endOf('day').valueOf(),
    [selectedDayKey, displayTimeZone]
  );
  const title = t('AVERAGE_HOURLY_DISCHARGE_PAST_30DAYS', { ns: 'performance' });

  const onDataFetched = useCallback((data: any) => {
    setDeviceBatteryLevels(data[0] as ITzBatteryLevel[]);
  }, []);

  const dataFetcher = useCallback(() => {
    if (props.id) {
      const params = { deviceId: props.id, to: deviceLevelDate, tz: displayTimeZone };
      return (options: RequestInitWithRetry) => {
        return Promise.all([
          getSelectedTzDateDeviceChargeLevel(params)(options)
        ]);
      };
    }
  }, [props.id, deviceLevelDate, displayTimeZone]);

  const { loading: isDataLoading } = useWorldRequest(dataFetcher, { onSuccess: onDataFetched });

  // The average discharge chart is bucketed by local calendar day, so a clicked bar's date names a local day
  const onSetDate = (date: string | Moment) => setSelectedDayKey(moment.utc(date).format('YYYY-MM-DD'));

  return (
    <div className="row details_information h-100">
      <div className="col-md-6 col-sm-12 col-12">
        <AverageDischargeChart
          data={props.data}
          title={title}
          className={"device-average-discharge-chart"}
          setDate={onSetDate}
          isLoading={props?.isLoading}
          timeZone={displayTimeZone}
        />
      </div>
      <div className="col-md-6 col-sm-12 col-12">
        <Loading isLoading={isDataLoading} transparentOverlay={true}>
          <DeviceLevelChart
            data={deviceBatteryLevels}
            displayTimeZone={displayTimeZone}
            date={deviceLevelDate}
          />
        </Loading>
      </div>
    </div>
  );
}
